import { useState } from "react";
import axios from "axios";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { ALL_TRADES } from "../lib/site-data";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  address: "",
  message: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name ||
      !form.email ||
      !form.phone ||
      !form.service ||
      !form.address ||
      !form.message
    ) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quotes`, form);
      toast.success("Quote sent! We'll be in touch within 24 hours.", {
        description:
          res.data?.email_sent === false
            ? "Saved to our system."
            : "A confirmation has been logged.",
      });
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error("Could not submit your request. Please try calling us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="relative rounded-lg border border-white/5 bg-[#2a3744] p-6 sm:p-8"
      data-testid="quote-form"
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="h-px w-6 bg-[#D4AF37]" />
        <span className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
          Project details
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Full Name"
          required
          value={form.name}
          onChange={update("name")}
          testId="form-name"
        />
        <Field
          label="Email"
          required
          type="email"
          value={form.email}
          onChange={update("email")}
          testId="form-email"
        />
        <Field
          label="Phone"
          required
          type="tel"
          value={form.phone}
          onChange={update("phone")}
          testId="form-phone"
        />
        <div>
          <Label required>Service Needed</Label>
          <Select value={form.service} onValueChange={update("service")}>
            <SelectTrigger
              className="mt-2 h-11 bg-[#1f2935] border-white/10 text-white text-sm focus:ring-[#D4AF37] focus:border-[#D4AF37]"
              data-testid="form-service"
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent className="bg-[#2a3744] border-white/10 text-white">
              {ALL_TRADES.map((t) => (
                <SelectItem
                  key={t}
                  value={t}
                  className="focus:bg-[#D4AF37]/15 focus:text-white text-sm"
                >
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Field
            label="Property Address"
            required
            value={form.address}
            onChange={update("address")}
            testId="form-address"
          />
        </div>
      </div>

      <div className="mt-4">
        <Label required>Project Details</Label>
        <textarea
          value={form.message}
          onChange={update("message")}
          rows={5}
          placeholder="Style, timeline, materials..."
          className="mt-2 w-full bg-[#1f2935] border border-white/10 rounded-md px-3.5 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition-colors resize-y"
          data-testid="form-message"
        />
      </div>

      <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-white/10">
        <p className="text-[11px] text-slate-500 leading-relaxed max-w-xs">
          We respond within 1 business day. Your information is never shared.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#D4AF37] text-[#1f2935] font-semibold text-[12px] uppercase tracking-[0.22em] hover:bg-[#B48600] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="form-submit"
        >
          {submitting ? "Sending..." : "Send Request"}
          <Send size={13} strokeWidth={2} />
        </button>
      </div>
    </form>
  );
};

const Label = ({ children, required }) => (
  <label className="text-[10px] uppercase tracking-[0.22em] text-slate-400">
    {children}
    {required && <span className="text-[#D4AF37] ml-1">*</span>}
  </label>
);

const Field = ({ label, required, type = "text", value, onChange, testId }) => (
  <div>
    <Label required={required}>{label}</Label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      className="mt-2 w-full h-11 bg-[#1f2935] border border-white/10 rounded-md px-3.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37] transition-colors"
      data-testid={testId}
    />
  </div>
);
